if (!customElements.get('myart-navigation-country')) {
  class MyArtNavigationCountry extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog?.showModal) return;
      this.form = this.querySelector('form');
      this.select = this.querySelector('select[name="country_code"]');
      this.trigger = this.querySelector('[data-country-open]');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.search = this.querySelector('[data-country-search]');
      this.clear = this.querySelector('[data-country-clear]');
      this.status = this.querySelector('[data-country-status]');
      this.empty = this.querySelector('[data-country-empty]');
      this.options = [...this.querySelectorAll('[data-country-code]')];
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      // Own focus paint before showModal() autofocus and after async restoration.
      // Text typing does not change the modality established by a pointer gesture.
      this.addEventListener('pointerdown', () => { this.dataset.countryInput = 'pointer'; }, { ...options, capture: true });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.dataset.countryInput = 'dismissal';
        else if (['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(event.key) ||
          (event.key === ' ' && event.target !== this.search)) this.dataset.countryInput = 'keyboard';
      }, { ...options, capture: true });
      this.trigger.addEventListener('click', () => {
        this.releaseFocusReturn();
        this.search.value = '';
        this.filter();
        window.MyArtModalMotion ? window.MyArtModalMotion.open(this.dialog) : this.dialog.showModal();
        this.trigger.setAttribute('aria-expanded', 'true');
        // Keep the full list visible on mobile instead of opening the keyboard.
        this.querySelector('[data-country-close]').focus({ preventScroll: true });
      }, options);
      this.querySelector('[data-country-close]').addEventListener('click', () => this.closeDialog(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeDialog(); }, options);
      this.dialog.addEventListener('close', () => {
        if (this.dialog.open) return;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.restoreFocus();
      }, options);
      this.search.addEventListener('input', () => this.filter(), options);
      this.clear?.addEventListener('click', () => {
        this.search.value = '';
        this.filter();
        this.search.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.options.forEach((button) => button.addEventListener('click', () => {
        const code = button.dataset.countryCode;
        if (![...this.select.options].some((option) => option.value === code)) return;
        this.select.value = code;
        this.form.requestSubmit();
      }, options));
      this.mobileQuery = window.matchMedia('(max-width: 989px)');
      this.mobileQuery.addEventListener('change', () => {
        this.releaseFocusReturn();
        if (this.dialog.open) this.dialog.close();
      }, options);
      this.querySelector('[data-country-fallback]').hidden = true;
      this.trigger.hidden = false;
      this.trigger.disabled = false;
    }

    closeDialog() {
      if (window.MyArtModalMotion) window.MyArtModalMotion.close(this.dialog); else this.dialog.close();
    }

    releaseFocusReturn() {
      this.focusReturn?.abort();
      this.focusReturn = null;
    }

    restoreFocus() {
      this.releaseFocusReturn();
      if (!this.isConnected || !this.trigger.getClientRects().length) return;
      this.trigger.focus({ preventScroll: true });
      const controller = new AbortController();
      this.focusReturn = controller;
      const options = { signal: controller.signal, capture: true };
      const release = () => this.releaseFocusReturn();
      // A background popup can finish closing after this native dialog and
      // restore stale focus. Preserve our completed return until the next user
      // intent, without competing with a newly opened foreground surface.
      for (const type of ['pointerdown', 'keydown', 'touchstart', 'click']) {
        document.addEventListener(type, release, { ...options, passive: true });
      }
      window.addEventListener('blur', event => { if (event.target === window) release(); }, options);
      let queued = false;
      const surfaces = 'dialog[open], [role="dialog"], [popover]';
      const previousSurfaces = new Set([...document.querySelectorAll(surfaces)].filter(element =>
        element.getClientRects().length && element.checkVisibility({ checkVisibilityCSS: true })));
      // A pre-existing consent popover behind our dialog is not a new focus
      // request. Pointer/keyboard intent still releases the guard immediately.
      const foreground = element => {
        const surface = element?.closest?.(surfaces);
        return surface && !previousSurfaces.has(surface);
      };
      document.addEventListener('focusin', event => {
        if (event.target === this.trigger) return;
        if (foreground(event.target)) { release(); return; }
        if (queued) return;
        queued = true;
        // Run after the other closer's focus call has completed, never in a
        // synchronous focus loop. New input/open/disconnect cancels this work.
        queueMicrotask(() => {
          queued = false;
          if (controller.signal.aborted) return;
          if (!this.isConnected || this.dialog.open || !this.trigger.getClientRects().length ||
              foreground(document.activeElement)) { release(); return; }
          if (document.activeElement !== this.trigger) this.trigger.focus({ preventScroll: true });
        });
      }, options);
    }

    disconnectedCallback() {
      this.releaseFocusReturn();
      if (this.dialog) window.MyArtModalMotion?.cancel(this.dialog);
      this.abortController?.abort();
      this.abortController = null;
      if (this.dialog?.open) this.dialog.close();
      this.trigger?.setAttribute('aria-expanded', 'false');
      delete this.dataset.countryInput;
    }

    normalize(value) {
      return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase().trim();
    }

    filter() {
      if (this.clear) this.clear.hidden = this.search.value.length === 0;
      const query = this.normalize(this.search.value);
      let count = 0;
      this.options.forEach((button) => {
        const matches = this.normalize(button.dataset.countrySearchText).includes(query);
        button.closest('[data-country-row]').hidden = !matches;
        if (matches) count += 1;
      });
      this.empty.hidden = count !== 0;
      this.status.textContent = this.status.dataset.countTemplate.replace('[count]', String(count));
      this.querySelector('.myart-nav__country-scroll').scrollTop = 0;
    }

    onKeydown(event) {
      // WebKit consumes Escape in search inputs before dialog's native cancel.
      // Dismiss this dialog explicitly and keep the underlying menu open.
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeDialog();
        return;
      }
      if (event.key === 'Tab') {
        const controls = [...this.dialog.querySelectorAll('button:not([disabled]), input:not([disabled])')]
          .filter((control) => control.getClientRects().length);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (first && event.shiftKey && event.target === first) {
          event.preventDefault();
          last.focus();
        } else if (last && !event.shiftKey && event.target === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      // Search Enter must never submit a stale selection to Shopify.
      if (event.target === this.search && event.key === 'Enter') event.preventDefault();
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      if (event.target !== this.search && !event.target.matches('[data-country-code]')) return;
      event.preventDefault();
      const visible = this.options.filter((button) => !button.closest('[data-country-row]').hidden);
      if (!visible.length) return;
      const index = visible.indexOf(event.target);
      const next = index === -1 ? (event.key === 'ArrowDown' ? 0 : visible.length - 1)
        : (index + (event.key === 'ArrowDown' ? 1 : -1) + visible.length) % visible.length;
      visible[next].focus();
    }
  }
  customElements.define('myart-navigation-country', MyArtNavigationCountry);
}
